import { useSelector } from 'react-redux';

import { useDeskId } from '@/hooks/useDeskId';
import { cn } from '@/lib/utils';
import { useScheduledMessages } from '@/modules/humanChat/hooks/useScheduledMessages';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectSessionId } from '@/redux/session/selectors';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

import ScheduledMessageCard from './ScheduledMessageCard';

interface Props {
  /**
   * `overlay` floats above the composer (like SavedResponsesPanel);
   * `inline` renders in the flow, above the resolved-conversation state card.
   */
  variant: 'overlay' | 'inline';
}

/** Pending scheduled messages for the open conversation. Self-hides when empty. */
const ScheduledMessagesList = ({ variant }: Props) => {
  const deskId = useDeskId();
  const sessionId = useSelector(selectSessionId);
  const { isEnabled, scheduledMessages, cancelScheduledMessage, cancelStatus } =
    useScheduledMessages(deskId, sessionId);

  const canWrite = useSelector((state: RootState) =>
    getPermissions(state, 'conversations', actions.WRITE)
  );

  if (!isEnabled || scheduledMessages.length === 0) {
    return null;
  }

  return (
    <div
      data-testid="scheduled-messages-list"
      className={cn(
        'max-h-64 divide-y divide-stroke overflow-y-auto rounded-lg border border-stroke bg-background',
        variant === 'overlay' && 'absolute bottom-full left-0 z-10 mb-2 w-full shadow-none',
        variant === 'inline' && 'mx-auto mt-4 w-full max-w-3xl'
      )}
    >
      {scheduledMessages.map((message) => (
        <ScheduledMessageCard
          key={message.scheduled_message_id}
          message={message}
          onCancel={cancelScheduledMessage}
          canCancel={canWrite}
          isCancelling={cancelStatus === 'pending'}
        />
      ))}
    </div>
  );
};

export default ScheduledMessagesList;
